// Types only: the library itself (5.5 MB) is loaded the first time a model is, not with the app
import type { MLCEngine, InitProgressReport } from "@mlc-ai/web-llm";

let engine: MLCEngine | null = null;
let currentModelId: string | null = null;
let isModelLoading = false;

// We use a singleton pattern to avoid reloading the model on every React render
export const loadLocalModel = async (
  modelId: string, 
  onProgress: (report: InitProgressReport) => void
): Promise<MLCEngine> => {
  // 1. Check WebGPU Support
  if (!(navigator as any).gpu) {
    throw new Error("WebGPU is not supported in this browser. Please use Chrome/Edge/Brave on a device with a GPU.");
  }

  if (engine && currentModelId === modelId) {
    // Model already loaded, verify instance is healthy
    console.log("WebLLM: Engine already loaded for", modelId);
    return engine;
  }
  
  if (isModelLoading) {
      throw new Error("A model is already loading. Please wait.");
  }

  console.log("WebLLM: Starting load for", modelId);
  isModelLoading = true;
  try {
      const { CreateMLCEngine } = await import("@mlc-ai/web-llm");
      // Reload or Load new model
      // initProgressCallback is called during download/cache steps
      engine = await CreateMLCEngine(modelId, {
          initProgressCallback: (report) => {
              console.log("WebLLM Progress:", report.text);
              onProgress(report);
          },
          logLevel: 'INFO' 
      });
      currentModelId = modelId;
      isModelLoading = false;
      console.log("WebLLM: Load complete");
      return engine;
  } catch (error) {
      console.error("WebLLM Load Error:", error);
      isModelLoading = false;
      throw error;
  }
};

export const generateLocal = async (prompt: string, systemPrompt?: string): Promise<string> => {
  if (!engine) throw new Error("Local model engine not initialized.");
  
  const messages = [
      { role: "system", content: systemPrompt || "You are a helpful AI assistant." },
      { role: "user", content: prompt }
  ];
  
  const reply = await engine.chat.completions.create({
      messages: messages as any,
      temperature: 0.7,
      // Some local models might need max_tokens to prevent infinite loops if they get stuck
      max_tokens: 2048, 
  });
  
  return reply.choices[0].message.content || "";
};

export const isLocalModelLoaded = (modelId: string) => {
    return engine !== null && currentModelId === modelId;
}